import { Button, Dialog, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { DialogContent, Cell } from "./styles";
import { CustomDate } from "../../../../../models/custom-date/custom-date";
import { ClientInvoiceDTO, ClientInvoiceFilter } from "../../../../../models/legal-entity-clients/client-invoices";
import { VendorInvoiceDTO } from "../../../../../models/legal-entity-vendors/vendor-invoices/vendor-invoice-dto";
import { TransactionRef } from "../../../../../models/transactions";
import { renderCellExpand } from "../../../../../reusable-components/RenderCellExpand/RenderCellExpand";
import { ColumnsHeaderTitle } from "../../../../../reusable-components/Table/styles";
import Table from "../../../../../reusable-components/Table/Table";
import { ClientInvoicesApiService } from "../../../../../services/legal-entity-clients";
import { VendorInvoicesApiService } from "../../../../../services/legal-entity-vendors";
import { FT_LECIT, FT_LEVIT } from "../../../../../utils/constants/filter-tables-constants";
import { convertDateToUTC, intlFormatDateShort } from "../../../../../utils/time-utils";
import DialogActions from "../../../../DialogActions/DialogActions";
import DialogTitle from "../../../../DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../LoadingOverlay/LoadingOverlay";

export const LinkExistingInovice = ({ item, handleNotifyParent, invoiceType, client, vendor }: Props) => {
  const { t } = useTranslation();
  const tRPClient = "pages.company_account_profile.tables.legal_entity_client_invoices";
  const translationClientFields = `${tRPClient}.fields`;
  const tRPVendor = "pages.company_account_profile.tables.legal_entity_vendor_invoices"; // tRP - translation root path
  const translationVendorFields = `${tRPVendor}.fields`;
  const theme = useTheme();

  const checkedInvoices = useRef([]);

  const service = useMemo(() => {
    return invoiceType === "client" ? new ClientInvoicesApiService() : new VendorInvoicesApiService();
  }, [invoiceType]);

  const DEFAULT_CURR_ITEM = useMemo(() => {
    return {};
  }, [item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      error: "",
      currItem: item ?? DEFAULT_CURR_ITEM,
      isTableMounted: false,
    };
  }, [DEFAULT_CURR_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleSave = useCallback(() => {
    setState({ ...INITIAL_STATE });
    handleNotifyParent(checkedInvoices.current[0]);
  }, [checkedInvoices, handleNotifyParent]);

  const handleClose = useCallback(() => {
    checkedInvoices.current = [];

    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        currItem: item,
      }));

    return () => {
      checkedInvoices.current = [];

      setState({ ...INITIAL_STATE });
    };
  }, [item, INITIAL_STATE]);

  const setTotalAmountCellStyles = (transaction: TransactionRef | null, invoiceAmount: number) => {
    return !!transaction && invoiceAmount === transaction?.amount
      ? { color: theme.palette.success.main }
      : { color: theme.palette.warning.main };
  };

  const columns: GridColumns = useMemo(() => {
    return invoiceType === "client"
      ? [
          {
            field: "client.legalEntity.name",
            renderCell: ({ row }) => (
              <>
                <Typography title={row?.client?.legalEntity?.name}>
                  {row?.client?.legalEntity?.code ? row?.client?.legalEntity?.code : row?.client?.legalEntity?.name}
                </Typography>
              </>
            ),
            renderHeader: () => (
              <Tooltip title={`${t("common_words.legal_entity")}`}>
                <ColumnsHeaderTitle>{`${t("common_words.legal_entity")}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 0.65,
          },
          {
            field: "client.name",
            valueGetter: ({ row }) => row.client?.name,
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.client`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.client`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 1,
          },
          {
            field: "invoiceNumber",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.invoice_number`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.invoice_number`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 1,
          },
          {
            field: "totalAmount",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.total_amount`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.total_amount`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            renderCell: ({ row: { transaction, totalAmount, currency } }) => (
              <Grid sx={setTotalAmountCellStyles(transaction, totalAmount)}>{`${Number(totalAmount).toFixed(2)} ${
                currency?.code
              }`}</Grid>
            ),
            align: "right",
            headerAlign: "right",
            flex: 1,
          },
          {
            field: "comment",
            headerName: t(`${translationClientFields}.comment`),
            valueGetter: ({ row }) => row?.comment,
            headerAlign: "center",
            renderCell: renderCellExpand,
            flex: 1.5,
          },
          {
            field: "issueDate",
            valueGetter: ({ row }) => intlFormatDateShort(new Date(row.issueDate)),
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.issue_date`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.issue_date`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            align: "center",
            flex: 1,
          },
          {
            field: "dueDate",
            valueGetter: ({ row }) => intlFormatDateShort(new Date(row.dueDate)),
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.due_date`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.due_date`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            align: "center",
            flex: 1,
          },
          {
            field: "vatCountry",
            renderCell: ({ row: { vatCountry, vatBases, currency } }) => (
              <Cell>
                <Typography>{vatCountry?.name}</Typography>
              </Cell>
            ),
            renderHeader: () => (
              <Tooltip title={`${t(`${translationClientFields}.vat_country`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationClientFields}.vat_country`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            align: "center",
            headerAlign: "center",
            flex: 0.7,
          },
        ]
      : [
          {
            field: "vendor.legalEntity.name",
            renderCell: ({ row }) => (
              <>
                <span title={row?.vendor?.legalEntity?.name}>
                  {row?.vendor?.legalEntity?.code ? row?.vendor?.legalEntity?.code : row?.vendor?.legalEntity?.name}
                </span>
              </>
            ),
            renderHeader: () => (
              <Tooltip title={`${t("common_words.legal_entity")}`}>
                <ColumnsHeaderTitle>{`${t("common_words.legal_entity")}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 0.65,
          },
          {
            field: "vendor.name",
            valueGetter: ({ row }) => row.vendor?.name,
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.vendor`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.vendor`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 1,
          },
          {
            field: "invoiceNumber",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.invoice_number`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.invoice_number`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            headerAlign: "center",
            flex: 1,
            // minWidth: 100,
          },
          {
            field: "totalAmount",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.total_amount`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.total_amount`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            renderCell: ({ row: { transaction, totalAmount, currency } }) => (
              <Grid sx={setTotalAmountCellStyles(transaction, totalAmount)}>{`${Number(totalAmount).toFixed(2)} ${
                currency?.code
              }`}</Grid>
            ),
            align: "right",
            headerAlign: "right",
            flex: 1,
          },
          {
            field: "comment",
            headerName: t(`${translationVendorFields}.comment`),
            valueGetter: ({ row }) => row?.comment,
            headerAlign: "center",
            flex: 1.5,
            renderCell: renderCellExpand,
          },
          {
            field: "issueDate",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.issue_date`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.issue_date`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            valueGetter: ({ row }) => intlFormatDateShort(new Date(row.issueDate)),
            headerAlign: "center",
            flex: 1,
          },
          {
            field: "dueDate",
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.due_date`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.due_date`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            valueGetter: (params) => intlFormatDateShort(new Date(params.row.dueDate)),
            headerAlign: "center",
            flex: 1,
            minWidth: 100,
          },
          {
            field: "vatCountry",
            renderCell: ({ row: { vatCountry, vatBases, currency } }) => (
              <Cell>
                <Typography>{vatCountry?.name}</Typography>
              </Cell>
            ),
            renderHeader: () => (
              <Tooltip title={`${t(`${translationVendorFields}.vat_country`)}`}>
                <ColumnsHeaderTitle>{`${t(`${translationVendorFields}.vat_country`)}`}</ColumnsHeaderTitle>
              </Tooltip>
            ),
            align: "center",
            headerAlign: "center",
            flex: 0.7,
          },
        ];
  }, [invoiceType]);

  const defaultItem: Partial<ClientInvoiceDTO | VendorInvoiceDTO> = useMemo(() => {
    return invoiceType === "client"
      ? {
          client: null,
          invoiceNumber: "",
          totalAmount: 0,
          comment: "",
          currency: null,
          issueDate: new Date(),
          dueDate: new Date(),
          vatCountry: null,
        }
      : {
          vendor: null,
          invoiceNumber: "",
          totalAmount: 0,
          comment: "",
          currency: null,
          issueDate: new Date(),
          dueDate: new Date(),
          vatCountry: null,
          vatBases: [],
          transactions: [],
        };
  }, [invoiceType]);

  return (
    <>
      <Button
        onClick={() => {
          setState((state) => ({
            ...state,
            isDialogOpen: true,
            isTableMounted: true,
          }));
        }}
        title={!item.legalEntity ? "Please select a legal entity" : "Link existing invoices"}
        sx={{ width: "fit-content", height: "fit-content", pointerEvents: "auto" }}
        disabled={!item.legalEntity}
      >
        Link existing invoices
      </Button>

      <Dialog
        open={state.isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth={"lg"}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Link existing invoices"}
            handleClose={handleClose}
          />
          <DialogContent>
            <Table
              tableHeight={500}
              translationRootPath={invoiceType === "client" ? tRPClient : tRPVendor}
              tableHeightAdjustment={120}
              columns={columns}
              displayTitle={false}
              serviceApi={service}
              defaultItem={defaultItem}
              filter={invoiceType === "client" ? FT_LECIT : FT_LEVIT}
              showNoItems={!state.isTableMounted}
              propsFilter={
                {
                  period: null,
                  legalEntityIds: [item?.legalEntity?.id],
                  withoutTransaction: true,
                  ...(invoiceType === "client"
                    ? { clientIds: client?.id ? [client.id] : [] }
                    : invoiceType === "vendor"
                    ? { vendorIds: vendor?.id ? [vendor.id] : [] }
                    : {}),
                  date: convertDateToUTC(CustomDate.ToDate(item.transactionDate) as Date),
                } as ClientInvoiceFilter
              }
              checkedRows={checkedInvoices}
              checkIsRowDeletable={(row) => false}
              checkIsRowEditable={(row) => false}
              maxRowsToSelect={1}
              CustomButtonToRender={() => {
                return <></>;
              }}
            />
          </DialogContent>
          <DialogActions
            handleCancel={handleClose}
            handleSave={handleSave}
            customSaveName={"Link"}
            loading={state.loading}
            error={state.error}
          />
        </LoadingOverlay>
      </Dialog>
    </>
  );
};
